/**
 * apps/web - Web版アプリケーション Composition Root (結合ブートストラップ)
 * @purin/ui-shell および各ユニットの公開ポートを生成・結線し、モジュラーモノリスとして起動する
 */

import {
  UiState,
  AppShell,
  CanvasInteraction,
  PhysicsControlsPanel,
  PresetModals,
  ExportModal,
  UrlImportModal,
  AudioSettingsPanel,
  DesktopAmbientPanel,
  LocalStorageUiShell,
  LicenseService,
  t,
} from '@purin/ui-shell';
import { createAudioSoundFxPort, AudioSoundFxService } from '@purin/audio-sound-fx';
import { createImageAssetPort } from '@purin/image-asset';
import { createMediaExportPort } from '@purin/media-export';
import { createDesktopAmbientPort } from '@purin/desktop-ambient';
import '@purin/ui-shell/styles/theme.css';

export async function bootstrapApp(): Promise<void> {
  console.log('[PurinPurin App] Initializing modular monolith web application...');

  const appRoot = document.getElementById('app');
  if (!appRoot) {
    throw new Error('#app root element not found.');
  }

  // 1. 状態ストアとUIポート、オーディオポート、画像アセットポート、メディアエクスポートポート、常駐ポートの初期化
  const uiState = UiState.getInstance();
  const licenseService = LicenseService.getInstance();
  const initialEdition = licenseService.isProActivated() ? 'pc_app_paid' : 'web_free';
  uiState.setEdition(initialEdition);

  const uiPort = new LocalStorageUiShell(initialEdition);
  const audioPort = createAudioSoundFxPort({ edition: initialEdition });
  const imagePort = createImageAssetPort();
  const mediaExportPort = createMediaExportPort();
  const ambientPort = createDesktopAmbientPort({ edition: initialEdition });

  // エディション変更・設定変更の追従
  uiState.subscribe((state) => {
    uiPort.setEdition(state.edition);
    if ('setEdition' in audioPort) {
      (audioPort as AudioSoundFxService).setEdition(state.edition);
    }
    audioPort.updateConfig({
      masterVolume: state.audio.masterVolume / 100,
      seVolume: state.audio.seVolume / 100,
      voiceVolume: 1.0,
      isMuted: state.audio.isMuted,
    });
  });

  // 2. モーダル類・サブパネルの前方宣言用ホルダー
  let presetModals: PresetModals;
  let exportModal: ExportModal;
  let urlImportModal: UrlImportModal;
  let canvasInteraction: CanvasInteraction;
  let audioPanel: AudioSettingsPanel;

  // 3. 基本シェル＆ヘッダーの組み立て
  const appShell = new AppShell({
    uiState,
    headerCallbacks: {
      onFileSelected: async (file: File) => {
        try {
          await imagePort.loadImage({ source: file, sourceType: 'user_upload' });
          canvasInteraction?.loadImageFromFile(file);
        } catch (err) {
          console.error('[PurinPurin App] Failed to load image asset:', err);
          alert((err as Error).message);
        }
      },
      onOpenUrlModal: () => {
        urlImportModal?.show();
      },
      onOpenPresetManager: () => {
        presetModals?.openManagerModal();
      },
      onOpenExportModal: () => {
        exportModal?.show();
      },
    },
  });

  appRoot.appendChild(appShell.getRootElement());

  // 4. メイン操作キャンバスの初期化＆マウント
  canvasInteraction = new CanvasInteraction(
    appShell.getCanvasArea(),
    uiState,
    {
      onPhysicsImpulse: (type) => {
        const eventType = type === 'poke' ? 'impact' : 'release';
        audioPort.handlePhysicsEvent({ type: eventType, intensity: 0.8 });
        if (type === 'poke') {
          audioPanel?.playSound('purin');
        } else if (type === 'bounce') {
          audioPanel?.playSound('poyon');
        }
      },
      onUpgradeRequested: (feature) => {
        appShell.getUpgradeModal().show(feature);
      },
    }
  );

  // 5. プリセット保存・管理モーダルの初期化＆マウント
  presetModals = new PresetModals(
    appShell.getModalContainer(),
    uiState,
    uiPort,
    {
      onPresetApplied: (_id, _name) => {
        canvasInteraction?.triggerPoke();
      },
      onTriggerPoke: () => {
        canvasInteraction?.triggerPoke();
      },
      onUpgradeRequested: (feature) => {
        appShell.getUpgradeModal().show(feature);
      },
    }
  );

  // 6. エクスポートモーダルの初期化＆マウント
  exportModal = new ExportModal(
    appShell.getModalContainer(),
    uiState,
    {
      onUpgradeRequested: (feature) => {
        appShell.getUpgradeModal().show(feature);
      },
      onStartExport: async (format, durationSec, onProgress) => {
        const sourceCanvas = canvasInteraction?.getCanvas();
        if (!sourceCanvas) {
          throw new Error('キャンバスが取得できませんでした。');
        }
        const isWebFree = uiState.getSnapshot().edition === 'web_free';
        const result = await mediaExportPort.exportAnimation(
          {
            sourceCanvas,
            format,
            durationMs: durationSec * 1000,
            fps: 30,
            applyWatermark: isWebFree,
            watermarkText: '🍮 Jiggly Touch',
          },
          (progress) => {
            const percent = Math.round(progress.progress * 100);
            onProgress(progress.stage, percent);
          }
        );

        // ブラウザ自動ダウンロード
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        console.log(`[PurinPurin App] Export completed and downloaded: ${result.fileName}`);

        return result;
      },
    }
  );

  // 6.5 Web画像URL読み込みモーダルの初期化＆マウント (有料版限定)
  urlImportModal = new UrlImportModal(
    appShell.getModalContainer(),
    uiState,
    {
      onUpgradeRequested: (feature) => {
        appShell.getUpgradeModal().show(feature);
      },
      onLoadUrl: async (url: string) => {
        try {
          await imagePort.loadImage({ source: url, sourceType: 'user_upload' });
          await canvasInteraction?.loadImageFromUrl(url);
          console.log('[PurinPurin App] Loaded image from URL successfully:', url);
        } catch (err) {
          console.error('[PurinPurin App] Failed to load image from URL:', err);
          throw err;
        }
      },
    }
  );

  // 7. 物理パラメータ＆自動揺れ調整パネルの初期化＆マウント
  new PhysicsControlsPanel(
    appShell.getControlPanel(),
    uiState,
    {
      onTriggerPoke: () => {
        canvasInteraction?.triggerPoke();
      },
      onOpenSavePreset: () => {
        presetModals?.openSaveModal();
      },
      onUpgradeRequested: (feature) => {
        appShell.getUpgradeModal().show(feature);
      },
      onPickWobblePos: () => {
        canvasInteraction?.setPickingWobblePos(true);
      },
      onAdjustWobbleRadius: () => {
        canvasInteraction?.showTemporaryRangeGuide();
      },
    }
  );

  // 8. オーディオ設定パネルの初期化＆マウント (タブ3へ)
  const tabAudioEl = appShell.getControlPanel().querySelector('#tabAudio') as HTMLElement;
  if (tabAudioEl) {
    audioPanel = new AudioSettingsPanel(tabAudioEl, uiState, {
      onUpgradeRequested: (feature) => {
        appShell.getUpgradeModal().show(feature);
      },
    });
  }

  // 9. デスクトップ常駐パネルの初期化＆マウント (タブ4へ)
  const tabAmbientEl = appShell.getControlPanel().querySelector('#tabAmbient') as HTMLElement;
  if (tabAmbientEl) {
    new DesktopAmbientPanel(
      tabAmbientEl,
      appShell.getModalContainer(),
      uiState,
      {
        onUpgradeRequested: (feature) => {
          appShell.getUpgradeModal().show(feature);
        },
        getSourceCanvas: () => canvasInteraction?.getCanvas() || null,
      }
    );
  }

  console.log('[PurinPurin App] Web application bootstrapped and mounted successfully.');
}

// DOMロード完了時、または既にパース済みの場合は即座に起動
if (typeof window !== 'undefined') {
  const init = () => {
    bootstrapApp().catch((err) => console.error('[PurinPurin App] Bootstrap failed:', err));
  };

  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
